import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";

import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import { EntityTypeIcon, Skeleton } from "@mapmycustomers/ui";

import TextSearchResult from "@app/types/TextSearchResult";
import useCloseOnOutsideScroll from "@app/util/hook/useCloseOnOutsideScroll";
import getEntityTypeDisplayName from "@app/util/ui/getEntityTypeDisplayName";

import styles from "./EntityMentionDropdown.module.scss";

interface EntityMentionDropdownProps {
  activeIndex: number;
  // Caret line box in viewport coords. The dropdown opens below `bottom` by
  // default and flips above `top` when there isn't room below it.
  anchorRect: { bottom: number; left: number; top: number };
  loading: boolean;
  onActiveIndexChange: (index: number) => void;
  onHide: () => void;
  onSelect: (entityType: EntityType, entityId: number, name: string) => void;
  results: TextSearchResult[];
}

const SKELETON_ROWS = [0, 1, 2];
// Gap between the caret line and the dropdown, and the minimum breathing room
// to keep between the dropdown and the viewport edges when clamping.
const ANCHOR_GAP_PX = 4;
const VIEWPORT_MARGIN_PX = 8;

const EntityMentionDropdown = ({
  activeIndex,
  anchorRect,
  loading,
  onActiveIndexChange,
  onHide,
  onSelect,
  results,
}: EntityMentionDropdownProps) => {
  const intl = useIntl();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const itemRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [coords, setCoords] = useState({
    left: anchorRect.left,
    top: anchorRect.bottom + ANCHOR_GAP_PX,
  });

  // Place below the caret by default; flip above when it would overflow the
  // bottom and there's more room above (e.g. a composer pinned to a modal's
  // foot — the case in the screenshot). Always clamp inside the viewport so the
  // last rows can't hide behind the edge. Runs before paint to avoid a flash.
  useLayoutEffect(() => {
    const element = dropdownRef.current;
    if (!element) {
      return;
    }
    const { offsetHeight: height, offsetWidth: width } = element;
    const viewportHeight = window.innerHeight;
    const viewportWidth = window.innerWidth;
    const spaceBelow = viewportHeight - anchorRect.bottom;
    const spaceAbove = anchorRect.top;
    const flipAbove = height + ANCHOR_GAP_PX > spaceBelow && spaceAbove > spaceBelow;
    const rawTop = flipAbove
      ? anchorRect.top - ANCHOR_GAP_PX - height
      : anchorRect.bottom + ANCHOR_GAP_PX;
    const top = Math.max(
      VIEWPORT_MARGIN_PX,
      Math.min(rawTop, viewportHeight - VIEWPORT_MARGIN_PX - height)
    );
    const left = Math.max(
      VIEWPORT_MARGIN_PX,
      Math.min(anchorRect.left, viewportWidth - VIEWPORT_MARGIN_PX - width)
    );
    setCoords({ left, top });
  }, [anchorRect.bottom, anchorRect.left, anchorRect.top, loading, results.length]);

  useEffect(() => {
    itemRefs.current[activeIndex]?.scrollIntoView({ block: "nearest" });
  }, [activeIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onHide();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onHide]);

  const isInsideDropdown = useCallback(
    (target: EventTarget | null) =>
      target instanceof Node && (dropdownRef.current?.contains(target) ?? false),
    []
  );
  useCloseOnOutsideScroll(true, onHide, isInsideDropdown);

  return createPortal(
    <div
      className={styles.mentionDropdown}
      ref={dropdownRef}
      style={
        {
          "--mention-anchor-left": `${coords.left}px`,
          "--mention-anchor-top": `${coords.top}px`,
        } as React.CSSProperties
      }
    >
      {loading && results.length === 0 ? (
        <div className={styles.mentionDropdownLoading}>
          {SKELETON_ROWS.map((row) => (
            <Skeleton
              active
              avatar={{ shape: "square", size: "small" }}
              key={row}
              paragraph={{ rows: 1, width: "70%" }}
              title={false}
            />
          ))}
        </div>
      ) : results.length === 0 ? (
        <div className={styles.mentionDropdownEmpty}>
          {intl.formatMessage({
            id: "component.entityMentions.dropdown.noResults",
            defaultMessage: "No results found",
            description: "Message shown when entity mention search returns no results",
          })}
        </div>
      ) : (
        results.map((result, index) => (
          <div
            className={`${styles.mentionDropdownItem} ${
              index === activeIndex ? styles.mentionDropdownItemActive : ""
            }`}
            key={`${result.entity}-${result.entityId}`}
            onMouseDown={(event) => {
              event.preventDefault();
              onSelect(result.entity, result.entityId, result.name);
            }}
            onMouseEnter={() => onActiveIndexChange(index)}
            ref={(element) => {
              itemRefs.current[index] = element;
            }}
          >
            <EntityTypeIcon className={styles.mentionDropdownItemIcon} entityType={result.entity} />
            <span className={styles.mentionDropdownItemName}>{result.name}</span>
            <span className={styles.mentionDropdownItemType}>
              {getEntityTypeDisplayName(intl, result.entity)}
            </span>
          </div>
        ))
      )}
    </div>,
    document.body
  );
};

export default EntityMentionDropdown;
